import { Link } from 'react-router'
import { AlertTriangle, ExternalLink } from 'lucide-react'
import { useSubscription, useCustomerPortal } from '@/hooks/useSubscription'
import { usePricing } from '@/hooks/usePricing'
import { UsageIndicator } from './UsageGate'
import { formatLongDate } from '@/lib/date'
import { useLabels } from '@/lib/terminology'

export function getStatusBadgeStyles(
  status: string
): { bg: string; text: string; label: string } {
  switch (status) {
    case 'trial':
      return {
        bg: 'bg-cyan-100 dark:bg-cyan-900/30',
        text: 'text-cyan-700 dark:text-cyan-300',
        label: 'Trial',
      }
    case 'active':
      return {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-700 dark:text-green-300',
        label: 'Active',
      }
    case 'past_due':
      return {
        bg: 'bg-red-100 dark:bg-red-900/30',
        text: 'text-red-700 dark:text-red-300',
        label: 'Past Due',
      }
    case 'canceled':
      return {
        bg: 'bg-zinc-100 dark:bg-zinc-800',
        text: 'text-zinc-600 dark:text-zinc-400',
        label: 'Canceled',
      }
    case 'expired':
      return {
        bg: 'bg-amber-100 dark:bg-amber-900/30',
        text: 'text-amber-700 dark:text-amber-300',
        label: 'Expired',
      }
    default:
      return {
        bg: 'bg-zinc-100 dark:bg-zinc-800',
        text: 'text-zinc-600 dark:text-zinc-400',
        label: status,
      }
  }
}

/**
 * Stripe-integrated billing page showing subscription status,
 * usage, and portal access
 */
export function StripeBillingPage() {
  const labels = useLabels()
  const {
    subscription,
    isLoading,
    isTrialActive,
    isTrialExpired,
    isPaid,
    isReadOnly,
    trialDaysRemaining,
  } = useSubscription()

  const { paidTiers } = usePricing()
  const portal = useCustomerPortal()

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-zinc-500 dark:text-zinc-400">
          Loading subscription...
        </div>
      </div>
    )
  }

  if (!subscription) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
            Billing
          </h1>
          <p className="text-zinc-500 mt-1">
            Manage your subscription and billing
          </p>
        </div>

        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 text-center">
          <p className="text-zinc-600 dark:text-zinc-400 mb-4">
            No subscription found. Start your free trial to get going!
          </p>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            View Plans
          </Link>
        </div>
      </div>
    )
  }

  const statusStyles = getStatusBadgeStyles(subscription.status)
  const isPastDue = subscription.status === 'past_due'

  // Find current plan name from pricing tiers
  const currentTier = paidTiers.find((t) => t.id === subscription.price_id)
  const planName =
    subscription.status === 'trial'
      ? 'Free Trial'
      : currentTier?.name || 'Unknown Plan'

  return (
    <div className="p-6 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-zinc-900 dark:text-zinc-100">
          Billing
        </h1>
        <p className="text-zinc-500 mt-1">
          Manage your subscription and billing
        </p>
      </div>

      {/* Past Due Warning Banner */}
      {isPastDue && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-red-800 dark:text-red-200">
              Payment Past Due
            </h3>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              Your last payment failed. Please update your payment method to
              avoid service interruption.
            </p>
            <button
              onClick={() => portal.mutate()}
              disabled={portal.isPending}
              className="mt-3 text-sm font-semibold text-red-700 dark:text-red-300 hover:text-red-800 dark:hover:text-red-200 underline"
            >
              {portal.isPending ? 'Loading...' : 'Update payment method'}
            </button>
          </div>
        </div>
      )}

      {/* Trial Expiry Warning */}
      {isTrialExpired && (
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-800 dark:text-amber-200">
              Trial Expired
            </h3>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              Your free trial has ended. Upgrade to continue creating recordings
              and adding guests.
            </p>
            <Link
              to="/pricing"
              className="mt-3 inline-block text-sm font-semibold text-amber-700 dark:text-amber-300 hover:text-amber-800 dark:hover:text-amber-200 underline"
            >
              View upgrade options
            </Link>
          </div>
        </div>
      )}

      {/* Current Plan Card */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Current Plan
            </h2>
            <div className="mt-4 flex items-center gap-3">
              <h3 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">
                {planName}
              </h3>
              <span
                className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${statusStyles.bg} ${statusStyles.text}`}
              >
                {statusStyles.label}
              </span>
            </div>

            {isTrialActive && trialDaysRemaining !== null && (
              <p className="mt-2 text-sm text-cyan-600 dark:text-cyan-400">
                {trialDaysRemaining} days remaining in trial
              </p>
            )}

            {isPaid && subscription.current_period_end && (
              <p className="mt-2 text-sm text-zinc-500 dark:text-zinc-400">
                Next billing date:{' '}
                {formatLongDate(subscription.current_period_end)}
              </p>
            )}
          </div>

          <Link
            to="/pricing"
            className="px-4 py-2 rounded-lg font-semibold text-sm bg-blue-600 hover:bg-blue-700 text-white transition-colors"
          >
            {isPaid ? 'Change Plan' : 'Upgrade'}
          </Link>
        </div>
      </div>

      {/* Usage Card */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 mb-6">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
          Usage
        </h2>
        <div className="grid sm:grid-cols-2 gap-6">
          <UsageIndicator type="recording" />
          <UsageIndicator type="guest" />
        </div>
        {isReadOnly && (
          <p className="mt-4 text-sm text-amber-600 dark:text-amber-400">
            You're in read-only mode. Upgrade to continue creating content.
          </p>
        )}
      </div>

      {/* Manage Subscription Card (for paid users) */}
      {isPaid && subscription.stripe_customer_id && (
        <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6 mb-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                Payment & Billing
              </h2>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                Manage your payment method, view invoices, and update billing
                details.
              </p>
            </div>
            <button
              onClick={() => portal.mutate()}
              disabled={portal.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm border border-zinc-300 dark:border-zinc-700 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50"
            >
              {portal.isPending ? (
                'Loading...'
              ) : (
                <>
                  <ExternalLink className="w-4 h-4" />
                  Manage Billing
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Plan Details */}
      <div className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 p-6">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-4">
          Plan Details
        </h2>
        <dl className="grid sm:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm text-zinc-500 dark:text-zinc-400">
              {labels.recordings} Limit
            </dt>
            <dd className="text-zinc-900 dark:text-zinc-100 font-medium">
              {subscription.recordings_limit} per billing period
            </dd>
          </div>
          <div>
            <dt className="text-sm text-zinc-500 dark:text-zinc-400">
              {labels.guests} Limit
            </dt>
            <dd className="text-zinc-900 dark:text-zinc-100 font-medium">
              {subscription.guests_limit} per billing period
            </dd>
          </div>
          <div>
            <dt className="text-sm text-zinc-500 dark:text-zinc-400">
              {labels.recordings} Used
            </dt>
            <dd className="text-zinc-900 dark:text-zinc-100 font-medium">
              {subscription.recordings_used}
            </dd>
          </div>
          <div>
            <dt className="text-sm text-zinc-500 dark:text-zinc-400">
              {labels.guests} Used
            </dt>
            <dd className="text-zinc-900 dark:text-zinc-100 font-medium">
              {subscription.guests_used}
            </dd>
          </div>
          {subscription.status === 'trial' && (
            <div>
              <dt className="text-sm text-zinc-500 dark:text-zinc-400">
                Trial Ends
              </dt>
              <dd className="text-zinc-900 dark:text-zinc-100 font-medium">
                {formatLongDate(subscription.trial_ends_at)}
              </dd>
            </div>
          )}
          <div>
            <dt className="text-sm text-zinc-500 dark:text-zinc-400">
              Member Since
            </dt>
            <dd className="text-zinc-900 dark:text-zinc-100 font-medium">
              {formatLongDate(subscription.created_at)}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  )
}
